import { Box, Button, Grid, IconButton, Typography } from "@mui/material";
import { Dispatch, RefObject, SetStateAction, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { PreferredBrandSize } from "schemas/dashboard";
import { CustomEvent, Map } from "schemas/functions";
import { v4 as uuidv4 } from "uuid";

import BentoBrandsAutocomplete from "components/BentoBrandsAutocomplete";
import LocationAutocomplete from "components/LocationAutocomplete";
import { UserLocation } from "components/LocationAutocomplete/schema";
import ProductsAutocomplete from "components/ProductsAutocomplete";
import QuestionChip from "components/Styled/QuestionChip";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

import styles from "../styles";
import DoNotRecommend, { DoNotRecommendHandle } from "./DoNotRecommend";
import ExcludeIncludeFilters from "./ExcludeIncludeFilters";

interface RecommendationFormProps {
  onboardingForm: Map;
  setOnboardingForm: Dispatch<SetStateAction<Map>>;
  saveOnboardingForm: (onboardingForm: Map, pageType: string) => void;
  setExcludeIncludeFilterHasChanged?: Dispatch<SetStateAction<boolean>>;
  doNotRecommendRef?: RefObject<DoNotRecommendHandle>;
  setIsDirty?: (isDirty: boolean) => void;
}

const RecommendationForm = ({
  onboardingForm,
  setOnboardingForm,
  saveOnboardingForm,
  setExcludeIncludeFilterHasChanged,
  doNotRecommendRef,
  setIsDirty,
}: RecommendationFormProps) => {
  const location = useLocation();
  const handleBrandSizeChange = (newSize: PreferredBrandSize) => {
    const copy = makeDeepCopy(onboardingForm);
    if (!copy.preferredBrandSize) {
      copy.preferredBrandSize = [];
    }
    const index = copy.preferredBrandSize?.findIndex(
      (size: string) => size === newSize,
    );
    if (index > -1) {
      copy.preferredBrandSize.splice(index, 1);
    } else {
      copy.preferredBrandSize.push(newSize);
    }

    setOnboardingForm(copy);
  };

  const addLocation = () => {
    trackEvent("Onboarding Form - Add Location Button Pressed");
    setOnboardingForm((prev) => {
      const copy = {
        ...prev,
        secondaryLocations: [
          ...(prev.secondaryLocations || []),
          { id: uuidv4() },
        ],
      };
      return copy;
    });
  };

  const removeLocation = (id: string | undefined) => {
    setOnboardingForm((prev) => {
      const copy = {
        ...prev,
        secondaryLocations: prev.secondaryLocations.filter(
          (location: UserLocation) => location.id !== id,
        ),
      };
      return copy;
    });
  };

  const handleLocationChange = (
    newLocation: UserLocation,
    id: string | undefined,
  ) => {
    setOnboardingForm((prev) => {
      const copy = { ...prev };
      copy.secondaryLocations = copy.secondaryLocations.map(
        (location: UserLocation) => {
          if (location.id === id) {
            return {
              ...newLocation,
              id,
            };
          }
          return location;
        },
      );
      return copy;
    });
  };

  const updatePastBrands = (newBrands: string[]) => {
    setOnboardingForm((prev) => {
      return {
        ...prev,
        brands: newBrands,
      };
    });
  };

  const handleSubmit = (event: CustomEvent) => {
    event.preventDefault();
    saveOnboardingForm(onboardingForm, location.pathname);
  };

  useEffect(() => {
    if (
      onboardingForm?.brands?.length > 0 &&
      !onboardingForm?.pastBrandValidation
    ) {
      setOnboardingForm((prev) => ({
        ...prev,
        pastBrandValidation: true,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingForm?.brands?.length]);

  return (
    <Box sx={{ mt: 6 }}>
      <form onSubmit={handleSubmit}>
        <Typography sx={styles.header} gutterBottom>
          Let us tailor your recommendations 🧵
        </Typography>

        <Box sx={styles.questionBox}>
          <Typography gutterBottom>
            Preferred brand size for outreach
          </Typography>
          <Typography variant="caption" color="textSecondary" paragraph>
            Select all that apply.
          </Typography>

          <Grid container gap={1}>
            <QuestionChip
              label="Small (more likely to respond)"
              isSelected={
                !!onboardingForm?.preferredBrandSize?.includes(
                  PreferredBrandSize.small,
                )
              }
              onClick={() => handleBrandSizeChange(PreferredBrandSize.small)}
            />
            <QuestionChip
              label="Larger, well-known brands (less likely to respond)"
              isSelected={
                !!onboardingForm?.preferredBrandSize?.includes(
                  PreferredBrandSize.large,
                )
              }
              onClick={() => handleBrandSizeChange(PreferredBrandSize.large)}
            />
          </Grid>
        </Box>

        <Box sx={styles.questionBox}>
          <Typography gutterBottom>
            Locations you frequently travel to (for potential brand
            partnerships)
          </Typography>
          <Grid container rowSpacing={0.5} sx={{ mb: 0.5 }}>
            {onboardingForm.secondaryLocations?.map(
              (location: UserLocation) => (
                <Grid
                  item
                  alignItems="center"
                  container
                  columnGap={0.5}
                  xs={12}
                  key={location.id}
                >
                  <Grid item xs>
                    <LocationAutocomplete
                      handleLocationChange={(newLocation: UserLocation) => {
                        handleLocationChange(newLocation, location.id);
                      }}
                      userLocation={location}
                    />
                  </Grid>
                  <Grid item xs="auto">
                    <IconButton
                      onClick={() => {
                        removeLocation(location.id);
                      }}
                      sx={{
                        color: "purple.main",
                        fontSize: 14,
                      }}
                    >
                      <Box component="i" className="fa-regular fa-trash" />
                    </IconButton>
                  </Grid>
                </Grid>
              ),
            )}
          </Grid>

          <Button
            size="small"
            onClick={addLocation}
            sx={{
              color: "purple.main",
              ml: -1,
            }}
          >
            Add Location{" "}
            <Box component="i" className="fa-solid fa-add" sx={{ ml: 1 }} />
          </Button>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography gutterBottom>
            List a few brands you've partnered with before
          </Typography>
          <BentoBrandsAutocomplete
            selectedBentoBrands={onboardingForm?.brands || []}
            updateSelectedBentoBrands={updatePastBrands}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography gutterBottom>
            Preferred product types for brand partnerships (e.g., linens, pet
            supplies, skincare, etc.)
          </Typography>
          <ProductsAutocomplete
            value={onboardingForm.preferredProducts || []}
            setValue={(newValue: string[]) => {
              setOnboardingForm((prev) => ({
                ...prev,
                preferredProducts: newValue,
              }));
            }}
          />
        </Box>

        <ExcludeIncludeFilters
          setExcludeIncludeFilterHasChanged={setExcludeIncludeFilterHasChanged}
          hideSecondaryText
        />

        <DoNotRecommend ref={doNotRecommendRef} setIsDirty={setIsDirty} />
      </form>
    </Box>
  );
};

export default RecommendationForm;
